import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input, Avatar } from 'antd';
import { DownloadOutlined, UserOutlined } from '@ant-design/icons';
import { Modal, Button, Table } from '../../../design-system';
import demographicsService from '../../../service/ApiServiceDemographics';
import { generateCSV, downloadCSV } from '../../../utils/currencyUtils';
import logger from '../../../utils/logger';



/**
 * Format tenure months into "X yrs Y mo" display string
 */
function formatTenure(months) {
    if (months === null || months === undefined) return '-';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years === 0) return `${remainingMonths} mo`;
    if (remainingMonths === 0) return `${years} yr${years !== 1 ? 's' : ''}`;
    return `${years} yr${years !== 1 ? 's' : ''} ${remainingMonths} mo`;
}

const columns = [
    {
        title: 'Employee',
        dataIndex: 'firstName',
        key: 'employee',
        width: 220,
        render: (_, record) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar size={32} src={record.profilePicture} icon={<UserOutlined />} />
                <span>{record.firstName} {record.lastName}</span>
            </div>
        ),
    },
    {
        title: 'Job Title',
        dataIndex: 'jobTitle',
        key: 'jobTitle',
        width: 160,
        render: (text) => text || '-',
    },
    {
        title: 'Department',
        dataIndex: 'department',
        key: 'department',
        width: 140,
    },
    {
        title: 'Office',
        dataIndex: 'office',
        key: 'office',
        width: 140,
        render: (_, record) => record.country ? `${record.office} (${record.country})` : record.office,
    },
    {
        title: 'Gender',
        dataIndex: 'gender',
        key: 'gender',
        width: 80,
        render: (text) => text || '-',
    },
    {
        title: 'Age',
        dataIndex: 'age',
        key: 'age',
        width: 70,
        render: (val) => val !== null && val !== undefined ? val : '-',
    },
    {
        title: 'Tenure',
        dataIndex: 'tenureMonths',
        key: 'tenure',
        width: 100,
        render: (val) => formatTenure(val),
    },
];

const DemographicsDrilldownModal = ({ visible, onClose, title, subtitle, snapshotDate, dimension, value }) => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [exporting, setExporting] = useState(false);
    const debounceRef = useRef(null);

    const fetchData = useCallback(async (pageNum, searchTerm) => {
        if (!dimension) return;
        setLoading(true);
        try {
            const result = await demographicsService.getDrilldown(snapshotDate, dimension, value, {
                page: pageNum,
                limit: 100,
                search: searchTerm || undefined,
            });
            setEmployees(result.employees || []);
            setTotal(result.pagination?.total || 0);
        } catch (err) {
            logger.error('[DrilldownModal] fetch error:', err);
            setEmployees([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [snapshotDate, dimension, value]);

    // Fetch when modal opens or page changes
    useEffect(() => {
        if (visible && dimension) {
            setPage(1);
            setSearch('');
            fetchData(1, '');
        }
    }, [visible, dimension, value, snapshotDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch on page change (but not on initial open)
    useEffect(() => {
        if (visible && dimension && page > 1) {
            fetchData(page, search);
        }
    }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (searchValue) => {
        setSearch(searchValue);
        setPage(1);
        fetchData(1, searchValue);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            fetchData(1, val);
        }, 300);
    };

    const handleExportCSV = async () => {
        setExporting(true);
        try {
            // Fetch all records for export
            const result = await demographicsService.getDrilldown(snapshotDate, dimension, value, {
                page: 1,
                limit: 10000,
                search: search || undefined,
            });
            const allEmployees = result.employees || [];

            const headers = ['Name', 'Email', 'Job Title', 'Department', 'Office', 'Country', 'Gender', 'Age', 'Tenure', 'Join Date'];
            const rows = allEmployees.map(emp => [
                `${emp.firstName} ${emp.lastName}`,
                emp.email,
                emp.jobTitle || '',
                emp.department || '',
                emp.office || '',
                emp.country || '',
                emp.gender || '',
                emp.age !== null && emp.age !== undefined ? emp.age : '',
                formatTenure(emp.tenureMonths),
                emp.joinDate || '',
            ]);

            const csv = generateCSV(headers, rows);
            const filename = `demographics-${dimension}${value ? '-' + String(value).replace(/\s+/g, '_') : ''}-${snapshotDate}.csv`;
            downloadCSV(csv, filename);
        } catch (err) {
            logger.error('[DrilldownModal] CSV export error:', err);
        } finally {
            setExporting(false);
        }
    };

    const displaySubtitle = subtitle || (total > 0 ? `${total} employee${total !== 1 ? 's' : ''}` : '');

    return (
        <Modal visible={visible} onClose={onClose} width="xl">
            <Modal.Header
                title={title || 'Employees'}
                subtitle={displaySubtitle}
                actions={
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleExportCSV}
                        loading={exporting}
                        variant="secondary"
                        size="small"
                    >
                        Export CSV
                    </Button>
                }
            />
            <Modal.Body padding="none">
                <div style={{ padding: '12px 16px 0' }}>
                    <Input.Search
                        placeholder="Search by name or email..."
                        value={search}
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                        allowClear
                        style={{ maxWidth: 360 }}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={employees}
                    loading={loading}
                    rowKey="id"
                    variant="borderless"
                    pagination={{
                        current: page,
                        pageSize: 100,
                        total,
                        onChange: (p) => setPage(p),
                        showTotal: (t) => `${t} employees`,
                        showSizeChanger: false,
                    }}
                    scroll={{ x: 900 }}
                />
            </Modal.Body>
            <Modal.Footer />
        </Modal>
    );
};

export default DemographicsDrilldownModal;
